import { defineStore } from 'pinia'
import { EXIT_REASONS, type ExitReason } from '@/api/medBilling'

interface SurveyState {
	answers: Record<string, unknown>
}

/**
 * Answers collected across the `/cancel/*` screens.
 *
 * Persisted to `sessionStorage` so an accidental reload mid-flow doesn't lose them — which also
 * means the state outlives a single cancel attempt. **Invariant: `answers` describes exactly one
 * pass through the flow.** `startFlow()` enforces that at the entry points on `/status`, because
 * the *exits* can't be hooked reliably (× / Back / closing the tab / logging out all leave the
 * answers behind). The invariant matters beyond tidiness: `cancellation_scope` is not
 * analytics-only — `/cancel/confirm` turns it into the cancel call's `cancellation_type`, so a
 * leaked answer from an abandoned pass would escalate a later cancel to `hard`. See ADR 0013.
 * `cancellation_reasons` isn't either: every exit call sends it as `exit_reason` (`exitReasons`,
 * DEV-36293), so a leaked answer would be recorded on the subscription as why the customer left.
 */
export const useSurveyStore = defineStore('survey', {
	state: (): SurveyState => ({
		answers: {},
	}),
	getters: {
		/**
		 * The `/cancel/reasons` answer as the `exit_reason` codes, in screen order — what the exit
		 * calls send. Empty when the screen was skipped or nothing was picked.
		 */
		exitReasons(state): ExitReason[] {
			const raw = state.answers['cancellation_reasons']
			const picked: unknown[] = Array.isArray(raw) ? raw : []
			return EXIT_REASONS.filter((code) => picked.includes(code))
		},
	},
	actions: {
		setAnswer(key: string, value: unknown) {
			this.answers = { ...this.answers, [key]: value }
		},
		/**
		 * Open a new cancel attempt — called from every `/status` entry point into `/cancel/*`.
		 * Same effect as `reset()`, named separately because the intent is the opposite end of the
		 * pass and that intent is what keeps the invariant above legible at the call sites.
		 */
		startFlow() {
			this.reset()
		},
		/** Close a pass: "No, I'd like to keep my plan", or a cancel the backend accepted. */
		reset() {
			this.answers = {}
		},
	},
	persist: {
		storage: sessionStorage,
		key: 'med-account-survey',
	},
})
